import styles from './selector-button.module.scss'
import { useEffect, useState } from 'react'
import { LocoScore } from '@/components/styleguide/LocoScore'
import {
  FILTER_LEASE_TYPES_PERSONAL,
  LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS,
  LEASE_PROFILE_FILTER_MILEAGES,
  LEASE_PROFILE_FILTER_STOCK_STATUS,
  LEASE_PROFILE_FILTER_TERMS,
} from '@/lib/constants'
import {
  addVatToPrice,
  capitaliseFirstLetter,
  formatPrice,
  formatWithCommas,
  getTermsAsYearsName,
} from '@/lib/utilities/search'
import { isProd } from '@/lib/utilities/system'
import { isLeaseProfileOptionAvailable } from '@/lib/utilities/vehicle'

export const SelectorButton = ({
  onChangeParameter,
  filter,
  filterValue,
  activeValue,
  availableFilters,
}) => {
  const [isAvailable, setIsAvailable] = useState(null)

  useEffect(() => {
    const available = isLeaseProfileOptionAvailable(
      availableFilters,
      `${
        filter === LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS ||
        filter === LEASE_PROFILE_FILTER_STOCK_STATUS
          ? filter
          : `${filter}s`
      }`,
      filterValue,
    )
    setIsAvailable(available)
  }, [availableFilters, filter, filterValue])

  const isActive = activeValue === filterValue

  const vehiclePrice = isAvailable
    ? availableFilters[
        `available${capitaliseFirstLetter(
          filter === LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS ||
            filter === LEASE_PROFILE_FILTER_STOCK_STATUS
            ? filter
            : `${filter}s`,
        )}`
      ].filter(object => object[filter] === filterValue)[0]
    : null

  const displayPrice = vehiclePrice
    ? vehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
      ? addVatToPrice(vehiclePrice.monthlyPayment)
      : vehiclePrice.monthlyPayment
    : 0

  const initialPayment = vehiclePrice
    ? vehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
      ? addVatToPrice(vehiclePrice.initialPaymentInMonths * vehiclePrice.monthlyPayment)
      : vehiclePrice.initialPaymentInMonths * vehiclePrice.monthlyPayment
    : 0

  const getAvailableValuesForFilter = () => {
    return (
      availableFilters[
        `available${capitaliseFirstLetter(
          filter === LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS ||
            filter === LEASE_PROFILE_FILTER_STOCK_STATUS
            ? filter
            : `${filter}s`,
        )}`
      ] || null
    )
  }

  const getPriceDifferenceBetweenValueAndActiveValue = price => {
    let difference = 0
    let activeVehiclePrice = null
    const availableValuesForFilter = getAvailableValuesForFilter()

    if (availableValuesForFilter) {
      activeVehiclePrice = availableValuesForFilter.filter(
        object => object[filter] === activeValue,
      )[0]
    }

    if (activeVehiclePrice) {
      const activeDisplayPrice =
        activeVehiclePrice.leaseType === FILTER_LEASE_TYPES_PERSONAL
          ? addVatToPrice(activeVehiclePrice.monthlyPayment)
          : activeVehiclePrice.monthlyPayment

      difference = price - activeDisplayPrice
    }

    return difference
  }

  const getFormattedDifference = difference => {
    return Math.sign(difference) >= 0
      ? `+ ${formatPrice(difference)}`
      : `- ${formatPrice(Math.abs(difference))}`
  }

  const getLabelForFilterValue = value => {
    if (filter === LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS) {
      return `${value} month${value === 1 ? '' : 's'}`
    }

    if (filter === LEASE_PROFILE_FILTER_TERMS) {
      return `${value >= 24 ? `${getTermsAsYearsName(value)} years` : `${value} months`}`
    }

    if (filter === LEASE_PROFILE_FILTER_MILEAGES) {
      return formatWithCommas(value)
    }

    if (filter === LEASE_PROFILE_FILTER_STOCK_STATUS) {
      return value > 0 ? 'In stock' : 'Factory order'
    }
  }

  return (
    <button
      type="button"
      onClick={() => onChangeParameter(filter, filterValue)}
      disabled={!isAvailable}
      data-active={!!isActive}
      className={`${styles['large-button']} ${isActive ? styles.active : ''}${!isProd() ? ` config-selector__${filter}-button--${filterValue}` : ''}`}>
      <div className={styles.score}>
        {(isAvailable && vehiclePrice?.rating != null) && (
          <LocoScore
            rating={vehiclePrice.rating}
            iconOnly
          />
        )}
      </div>
      <p>{getLabelForFilterValue(filterValue)}</p>
      <p>
        <span className={styles['small-text']}>
          {isAvailable
            ? filter === LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS
              ? formatPrice(initialPayment)
              : isActive
                ? `${formatPrice(displayPrice)}/pm`
                : `${getFormattedDifference(
                    getPriceDifferenceBetweenValueAndActiveValue(displayPrice),
                  )}/pm`
            : 'Unavailable'}
        </span>
      </p>
    </button>
  )
}
